import { Box, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users } from "../../API/Users";
import { TDate } from "../../TDate";
import { Chart } from "../../components/Chart";

type TileProps = {
  title: string;
  type: string[];
  sortBy: string;
  chartTitle: string;
};

export default function Tile({ type, sortBy, title, chartTitle }: TileProps) {
  const userAPI = useMemo(() => new Users(), []);
  const [infoData, setInfoData] = useState([]);
  const [chartConfig, setChartConfig] = useState<any>(null);

  const chartRef = useRef<any>(null);
  const containerRef = useRef<any>(null);
  const [numberOfUserWithAccountParams, setNumberOfUserWithAccountParams] =
    useState<any>(null);

  const getData = useCallback(async () => {
    // const accountParams = {
    //   filters: [
    //     {
    //       dimension: "active",
    //       segments: [true],
    //     },
    //     {
    //       dimension: "level",
    //       segments: type,
    //     },
    //   ],
    //   //TODO: ask to filippo why this is not working.
    //   //temporary fix: sort by creation time after fetch
    //   sort: {
    //     dimension: sortBy,
    //     rev: false,
    //   },
    // };

    // timeframe: last 90 days
    var today = new Date();
    var end = new Date(
      Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        23,
        59
      )
    );
    let dayJsInstance = dayjs(end);
    var past = dayJsInstance.add(-90, "day").toDate();
    var start = new Date(
      Date.UTC(
        past.getUTCFullYear(),
        past.getUTCMonth(),
        past.getUTCDate(),
        0,
        1
      )
    );
    let timeframe = {
      start: start.getTime(),
      end: end.getTime(),
    };

    const paramsLast90Days = {
      filters: [
        {
          dimension: "active",
          segments: [true],
        },
        {
          dimension: "level",
          segments: type,
        },
      ],
      ranges: [
        {
          dimension: sortBy,
          segments: [
            {
              max: timeframe.end,
              min: timeframe.start,
            },
          ],
        },
      ],
      sort: {
        dimension: sortBy,
        rev: true,
      },
    };

    var paramsTotal = {
      filters: [
        {
          dimension: "active",
          segments: [true],
        },
        {
          dimension: "level",
          segments: type,
        },
      ],
      sort: {
        dimension: sortBy,
        rev: true,
      },
    };

    try {
      //#region - infoData
      let ids = await userAPI.selectUsers(paramsTotal);
      setNumberOfUserWithAccountParams(ids.data.total);

      ids = await userAPI.selectUsers(paramsTotal);
      ids = ids.data.ids;
      let data = await userAPI.fetchUserProperties(
        ["creationTime", "group", "owner", "levelUpdate"],
        ids
      );
      data = data.data.rows;
      data.sort((a: any, b: any) => b.levelUpdate - a.levelUpdate);

      data = data.slice(0, 5);
      setInfoData(data);
      //#endregion-----------

      //#region - chartData
      ids = await userAPI.selectUsers(paramsLast90Days);
      ids = ids.data.ids;
      data = await userAPI.fetchUserProperties(
        ["levelUpdate", "creationTime"],
        ids
      );
      data = data.data.rows;
      data = _prepareData(timeframe, data, sortBy, type[0], title);
      const _chartConfig: Highcharts.Options = {
        chart: {
          animation: false,
          zooming: {
            mouseWheel: true,
            type: "x",
          },
          type: "column",
          height: (9 / 21) * 100 + "%", // 16:9 ratio
          
          width: null,
        },
        tooltip: {
          enabled: true,
          shared:true
        },
        credits: { enabled: false },
        exporting: { enabled: false },
        legend: { enabled: false },
        navigator: { enabled: false },
        rangeSelector: { enabled: false },
        series: data.series,
        scrollbar: { enabled: false },
        title: {
          align: "left",
          style: {
            fontFamily: "Open Sans",
            fontSize: ".9em",
          },
          text: chartTitle,
        },
        xAxis: {
          type: "datetime",
          minRange: 24 * 60 * 60 * 1000,
          endOnTick: false,
        },
        yAxis: {
          allowDecimals: false,
          endOnTick: false,
          min: 0,
          opposite: false,
          startOnTick: false,
          title: { text: null },
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 500,
              },
              chartOptions: {
                legend: {
                  align: "center",
                  verticalAlign: "bottom",
                  layout: "horizontal",
                },
              },
            },
          ],
        },
      };
      setChartConfig(_chartConfig);

      //#endregion -------
    } catch (error: any) {
      alert(error.message);
      throw new Error(error as any);
    }
  }, [chartTitle, sortBy, title, type, userAPI]);
  useEffect(() => {
    if (type.length > 0) {
      getData();
    }
  }, [getData, type.length]);

  return chartConfig && infoData ? (
    <Box display={"flex"} height={"100%"} flexDirection={"column"}>
      <Box
        p={1}
        display={"flex"}
        gap={1}
        alignItems={"center"}
        boxShadow={3}
        borderRadius={1}
      >
        <Typography variant="h4">
          <strong>{numberOfUserWithAccountParams}</strong>
        </Typography>
        <Typography>{title}</Typography>
      </Box>
      <Box p={1} display={"flex"} minHeight={0} gap={1} height={"100%"}>
        <Box overflow={"auto"} flex={1} width={"100%"} alignItems={"center"}>
          <Box
            flex={1}
            display={"flex"}
            width={"100%"}
            flexDirection={"column"}
            justifyContent={"space-around"}
            height={"100%"}
          >
            {infoData.map((item: any, key) => (
              <Box
                key={key}
                display={"flex"}
                gap={1}
                width={"100%"}
                justifyContent={"space-between"}
              >
                <Typography
                  flex={1}
                  whiteSpace={"nowrap"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                >
                  {TDate.daysToIso8601(
                    TDate.millisecondsToDays(sortBy==="levelUpdate"? item.levelUpdate:  item.creationTime)
                  )}
                </Typography>
                <Typography
                  flex={2}
                  whiteSpace={"nowrap"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                >
                  {item.group}
                </Typography>
                <Typography
                  flex={1}
                  whiteSpace={"nowrap"}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                >
                  {item.owner}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box flex={1} height={"100%"} width={"100%"} ref={containerRef}>
          <Chart ref={chartRef} options={chartConfig} />
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      height={"100%"}
      width={"100%"}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <CircularProgress color="secondary" />
    </Box>
  );
}

const _prepareData = (
  timeframe: any,
  response: any,
  conversionProperty: any,
  type: any,
  title: any
) => {
  var data = response;
  var datetime: any = null;
  var day: any = null;
  var days: any = {};
  //   var key: any = null;
  // last 90 days
  var last = new Date(timeframe.end);
  var end = new Date(
    Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate())
  ).getTime();

  let dayJsInstance = dayjs(end);
  var first = dayJsInstance.add(-90, "day").toDate();

  var start = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate())
  ).getTime();
  for (let day: any = start; day <= end; day = day + 24 * 60 * 60 * 1000) {
    days[day] = {
      timestamp: day,
      total: 0,
    };
  }
  // group accounts by conversionProperty date
  for (var i = 0, length = data.length; i < length; i++) {
    // property is datetime. Keys are generated using only date
    // information: the day
    datetime = new Date(data[i][conversionProperty]);
    day = new Date(
      Date.UTC(
        datetime.getUTCFullYear(),
        datetime.getUTCMonth(),
        datetime.getUTCDate()
      )
    ).getTime();

    days[day].total += 1;
  }
  // ordering day from oldest to newest
  var orderedDays: any[] = [];
  for (day in days) {
    orderedDays.push(days[day]);
  }
  orderedDays.sort(function (a, b) {
    if (a.timestamp > b.timestamp) {
      return 1;
    }
    if (a.timestamp < b.timestamp) {
      return -1;
    }
    return 0;
  });
  // preparing serie
  data = [];
  for (let i = 0; i < orderedDays.length; i++) {
    let record = orderedDays[i];
    if (record.total) {
      data.push([record.timestamp, record.total]);
    }
  }
  var series = [
    {
      id: type,
      name: title,
      animation: false,
      color: "#ddd",
      data: data,
      dataGrouping: {
        units: [["day", [1]]],
      },
      pointWidth: 3,
      type: "column",
      enableMouseTracking: false // This will disable the tooltip for this series
    },
    {
      id: type,
      name: title,
      animation: false,
      color: type === "SUBSCRIBER" ? "green" : "orange",
      data: data,
      dataGrouping: {
        units: [["day", [1]]],
      },
      lineWidth: 0,
      marker: {
        enabled: true,
        radius: 4,
      },
  
      states: {
        hover: {
          lineWidthPlus: 0,
        },
      },
      type: "line",
    },
  ];

  var preparedData = {
    series: series,
  };

  return preparedData;
};
